/**
 * ImageLightbox.jsx
 * Full-size image viewer.
 *
 * Generated images cost real money and get judged on detail — whether the type
 * is spelled right, whether the screenshot came through sharp — so a thumbnail
 * is not enough to decide with.
 *
 * Click anywhere, or press Escape, to close.
 */

import { useEffect, useState } from 'react';
import { Link, Plus } from './Icons.jsx';

export default function ImageLightbox({
  src,
  alt = '',
  caption,
  isVideo = false,
  onClose,
  // Anything the screen wants under the picture — the library puts the alt
  // text editor here. Optional, so every other caller is unaffected.
  footer = null,
}) {
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    // The page behind must not scroll while the viewer is open.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="lightbox" onClick={onClose}>
      <div className="lightbox__bar" onClick={(e) => e.stopPropagation()}>
        {caption && <span className="lightbox__caption">{caption}</span>}
        <div className="btn-row">
          {!isVideo && (
            <button className="btn btn--sm" onClick={() => setZoomed((z) => !z)}>
              <Plus size={15} /> {zoomed ? 'Fit to screen' : 'Actual size'}
            </button>
          )}
          <a className="btn btn--sm" href={src} target="_blank" rel="noreferrer">
            <Link size={15} /> Open in new tab
          </a>
          <button className="btn btn--sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <div className={`lightbox__stage${zoomed ? ' is-zoomed' : ''}`}>
        {isVideo ? (
          // Video plays here rather than zooming: seeing it move is the point.
          <video src={src} controls autoPlay playsInline onClick={(e) => e.stopPropagation()} />
        ) : (
          <img
            src={src}
            alt={alt}
            onClick={(e) => {
              e.stopPropagation();
              setZoomed((z) => !z);
            }}
          />
        )}
      </div>

      {footer && (
        <div className="lightbox__footer" onClick={(e) => e.stopPropagation()}>
          {footer}
        </div>
      )}
    </div>
  );
}
